import { useEffect, useLayoutEffect, useRef, useState } from "react";

const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID || "").trim();
const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_LAYER_ID = "callsect-google-identity-layer";
const GOOGLE_BUTTON_SIZE = "medium";
const GOOGLE_BUTTON_MAX_WIDTH = 400;
const GOOGLE_BUTTON_TEXTS = ["signin_with", "signup_with", "continue_with"];
const GOOGLE_MOBILE_QUERY = "(max-width: 900px)";

let googleScriptPromise;
let googleInitializationPromise;
let googleCredentialReceiver;
let persistentGoogleLayer;
let persistentGoogleHost;
let persistentGoogleWidth = 0;
let persistentGoogleRenderQueue = Promise.resolve();
let persistentGoogleAttachmentOwner = null;

function loadGoogleIdentityServices() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    const script = existing || document.createElement("script");
    const handleLoad = () => {
      if (window.google?.accounts?.id) resolve(window.google);
      else reject(new Error("Google Identity Services did not initialise."));
    };
    const handleError = () => reject(new Error("Google Identity Services could not be loaded."));

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    if (!existing) {
      script.id = GOOGLE_SCRIPT_ID;
      script.src = "https://accounts.google.com/gsi/client?hl=en";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    googleScriptPromise = undefined;
    throw error;
  });

  return googleScriptPromise;
}

function initialiseGoogleIdentityServices() {
  if (googleInitializationPromise) return googleInitializationPromise;

  googleInitializationPromise = loadGoogleIdentityServices()
    .then((google) => {
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => googleCredentialReceiver?.(response),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      return google;
    })
    .catch((error) => {
      googleInitializationPromise = undefined;
      throw error;
    });

  return googleInitializationPromise;
}

function normaliseButtonText(text) {
  return GOOGLE_BUTTON_TEXTS.includes(text) ? text : "continue_with";
}

function expectedAuthButtonWidth() {
  if (typeof window === "undefined") return GOOGLE_BUTTON_MAX_WIDTH;
  return Math.max(220, Math.min(GOOGLE_BUTTON_MAX_WIDTH, Math.round(window.innerWidth - 32)));
}

function isMobileGoogleViewport() {
  return typeof window !== "undefined" && window.matchMedia?.(GOOGLE_MOBILE_QUERY).matches;
}

function getPersistentGoogleLayer() {
  if (persistentGoogleLayer) return persistentGoogleLayer;
  if (typeof document === "undefined") return null;

  const layer = document.createElement("div");
  layer.id = GOOGLE_LAYER_ID;
  layer.className = "google-identity-persistent-layer";
  layer.setAttribute("aria-hidden", "true");
  layer.setAttribute("inert", "");

  const host = document.createElement("div");
  host.className = "google-identity-control__persistent-host";
  host.dataset.googleText = "signin_with";

  GOOGLE_BUTTON_TEXTS.forEach((buttonText) => {
    [
      ["outline", "light"],
      ["filled_black", "dark"],
    ].forEach(([theme, themeName]) => {
      const target = document.createElement("div");
      target.className = [
        "google-identity-control__persistent-button",
        `google-identity-control__persistent-button--${buttonText.replace("_with", "")}`,
        `google-identity-control__persistent-button--${themeName}`,
      ].join(" ");
      target.dataset.googleButtonText = buttonText;
      target.dataset.googleButtonTheme = theme;
      host.appendChild(target);
    });
  });

  layer.appendChild(host);
  document.body.appendChild(layer);
  persistentGoogleLayer = layer;
  persistentGoogleHost = host;
  return layer;
}

function waitForGoogleButtonPaint(target) {
  return new Promise((resolve) => {
    let finished = false;
    let observer;
    let fallbackTimer;
    let boundIframe;

    const finish = () => {
      if (finished) return;
      finished = true;
      observer?.disconnect();
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    };

    const bindIframe = () => {
      const iframe = target.querySelector("iframe");
      if (!iframe || iframe === boundIframe) return Boolean(iframe);
      boundIframe = iframe;
      iframe.addEventListener("load", finish, { once: true });
      fallbackTimer = window.setTimeout(finish, 700);
      return true;
    };

    if (!bindIframe() && typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        if (bindIframe()) observer.disconnect();
      });
      observer.observe(target, { childList: true, subtree: true });
    } else if (!boundIframe) {
      fallbackTimer = window.setTimeout(finish, 700);
    }
  });
}

function parkPersistentGoogleLayer() {
  const layer = getPersistentGoogleLayer();
  if (!layer) return;
  layer.classList.remove("is-active", "is-disabled");
  layer.setAttribute("aria-hidden", "true");
  layer.setAttribute("inert", "");
  layer.dataset.googleSurface = "parked";
  layer.style.removeProperty("width");
  layer.style.removeProperty("height");
  layer.style.removeProperty("transform");
}

function renderPersistentGoogleButtons(width) {
  const nextWidth = Math.max(220, Math.min(GOOGLE_BUTTON_MAX_WIDTH, Math.round(width)));

  persistentGoogleRenderQueue = persistentGoogleRenderQueue
    .catch(() => null)
    .then(async () => {
      const google = await initialiseGoogleIdentityServices();
      const layer = getPersistentGoogleLayer();
      const host = persistentGoogleHost;
      if (!layer || !host) throw new Error("Google Sign-In host could not be prepared.");

      if (persistentGoogleWidth === nextWidth && host.dataset.googleRendered === "true") {
        return host;
      }

      // Width changes rebuild while the persistent layer is parked. Normal
      // route entry only repositions the already-painted iframe layer.
      parkPersistentGoogleLayer();
      persistentGoogleAttachmentOwner = null;
      layer.style.setProperty("--google-identity-width", `${nextWidth}px`);
      host.style.width = `${nextWidth}px`;
      host.dataset.googleRendered = "false";

      const paintPromises = [];
      host.querySelectorAll("[data-google-button-text]").forEach((target) => {
        target.replaceChildren();
        google.accounts.id.renderButton(target, {
          type: "standard",
          theme: target.dataset.googleButtonTheme,
          size: GOOGLE_BUTTON_SIZE,
          shape: "rectangular",
          text: target.dataset.googleButtonText,
          logo_alignment: "left",
          width: nextWidth,
          locale: "en",
        });
        paintPromises.push(waitForGoogleButtonPaint(target));
      });

      await Promise.all(paintPromises);
      persistentGoogleWidth = nextWidth;
      host.dataset.googleRendered = "true";
      return host;
    });

  return persistentGoogleRenderQueue;
}

function positionPersistentGoogleLayer(slot, { text, disabled }) {
  const layer = getPersistentGoogleLayer();
  const host = persistentGoogleHost;
  if (!layer || !host || !slot) return;

  const rect = slot.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  host.dataset.googleText = normaliseButtonText(text);
  layer.dataset.googleSurface = slot.closest(".login-page") ? "login" : "default";
  layer.style.width = `${Math.round(rect.width)}px`;
  layer.style.height = `${Math.round(rect.height)}px`;
  layer.style.transform = `translate3d(${Math.round(rect.left)}px, ${Math.round(rect.top)}px, 0)`;
  layer.classList.toggle("is-disabled", Boolean(disabled));
  layer.classList.add("is-active");
  layer.removeAttribute("aria-hidden");
  layer.removeAttribute("inert");
}

export const googleIdentityIsConfigured = Boolean(GOOGLE_CLIENT_ID);

export function prewarmGoogleIdentityServices() {
  if (!GOOGLE_CLIENT_ID || typeof window === "undefined") return Promise.resolve(null);
  // The fixed persistent layer is a desktop-only optimisation. Mobile browsers
  // have a separate visual viewport, so the button stays in normal document
  // flow there instead of being projected from body coordinates.
  if (isMobileGoogleViewport()) return initialiseGoogleIdentityServices().then(() => window.google);
  return renderPersistentGoogleButtons(expectedAuthButtonWidth()).then(() => window.google);
}

export default function GoogleIdentityButton({
  onCredential,
  onError,
  disabled = false,
  text = "continue_with",
  className = "",
}) {
  const slotRef = useRef(null);
  const mobileHostRef = useRef(null);
  const mobileLightRef = useRef(null);
  const mobileDarkRef = useRef(null);
  const ownerRef = useRef(Symbol("google-identity-slot"));
  const credentialHandlerRef = useRef(onCredential);
  const errorHandlerRef = useRef(onError);
  const textRef = useRef(text);
  const disabledRef = useRef(disabled);
  const [loadError, setLoadError] = useState("");
  const [mobileViewport, setMobileViewport] = useState(isMobileGoogleViewport);

  useEffect(() => {
    credentialHandlerRef.current = onCredential;
    errorHandlerRef.current = onError;
    textRef.current = text;
    disabledRef.current = disabled;
  }, [onCredential, onError, text, disabled]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia(GOOGLE_MOBILE_QUERY);
    const syncViewport = () => setMobileViewport(media.matches);
    syncViewport();
    if (media.addEventListener) media.addEventListener("change", syncViewport);
    else media.addListener?.(syncViewport);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", syncViewport);
      else media.removeListener?.(syncViewport);
    };
  }, []);

  useEffect(() => {
    const receiveCredential = (response) => {
      if (!response?.credential) {
        errorHandlerRef.current?.(new Error("Google did not return a sign-in credential."));
        return;
      }
      credentialHandlerRef.current?.(response.credential);
    };
    googleCredentialReceiver = receiveCredential;

    return () => {
      if (googleCredentialReceiver === receiveCredential) googleCredentialReceiver = undefined;
    };
  }, []);

  useLayoutEffect(() => {
    if (!GOOGLE_CLIENT_ID || mobileViewport || !slotRef.current) return undefined;

    let active = true;
    let resizeObserver;
    let frame = 0;
    const slot = slotRef.current;
    const owner = ownerRef.current;
    const measuredWidth = slot.getBoundingClientRect().width;
    const width = measuredWidth > 0 ? measuredWidth : expectedAuthButtonWidth();

    const syncPosition = () => {
      if (!active || persistentGoogleAttachmentOwner !== owner) return;
      positionPersistentGoogleLayer(slot, {
        text: textRef.current,
        disabled: disabledRef.current,
      });
    };

    const schedulePosition = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(syncPosition);
    };

    renderPersistentGoogleButtons(width)
      .then(() => {
        if (!active) return;
        persistentGoogleAttachmentOwner = owner;
        syncPosition();
        setLoadError("");

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(schedulePosition);
          resizeObserver.observe(slot);
        }
        window.addEventListener("resize", schedulePosition, { passive: true });
        window.addEventListener("scroll", schedulePosition, { passive: true, capture: true });
      })
      .catch((error) => {
        if (!active) return;
        setLoadError("Google Sign-In is temporarily unavailable.");
        errorHandlerRef.current?.(error);
      });

    return () => {
      active = false;
      resizeObserver?.disconnect();
      window.removeEventListener("resize", schedulePosition);
      window.removeEventListener("scroll", schedulePosition, true);
      if (frame) window.cancelAnimationFrame(frame);
      if (persistentGoogleAttachmentOwner === owner) {
        persistentGoogleAttachmentOwner = null;
        parkPersistentGoogleLayer();
      }
    };
  }, [mobileViewport]);

  useLayoutEffect(() => {
    if (mobileViewport || persistentGoogleAttachmentOwner !== ownerRef.current || !slotRef.current) return;
    positionPersistentGoogleLayer(slotRef.current, { text, disabled });
  }, [text, disabled, mobileViewport]);

  useLayoutEffect(() => {
    if (!GOOGLE_CLIENT_ID || !mobileViewport) return undefined;
    const host = mobileHostRef.current;
    const lightTarget = mobileLightRef.current;
    const darkTarget = mobileDarkRef.current;
    if (!host || !lightTarget || !darkTarget) return undefined;

    let active = true;
    host.classList.remove("is-ready");

    const measuredWidth = host.getBoundingClientRect().width;
    const width = Math.max(
      220,
      Math.min(GOOGLE_BUTTON_MAX_WIDTH, Math.round(measuredWidth > 0 ? measuredWidth : expectedAuthButtonWidth()))
    );
    const buttonText = normaliseButtonText(text);

    initialiseGoogleIdentityServices()
      .then(async (google) => {
        if (!active) return;

        [
          [lightTarget, "outline"],
          [darkTarget, "filled_black"],
        ].forEach(([target, theme]) => {
          target.replaceChildren();
          google.accounts.id.renderButton(target, {
            type: "standard",
            theme,
            size: GOOGLE_BUTTON_SIZE,
            shape: "rectangular",
            text: buttonText,
            logo_alignment: "left",
            width,
            locale: "en",
          });
        });

        await Promise.all([
          waitForGoogleButtonPaint(lightTarget),
          waitForGoogleButtonPaint(darkTarget),
        ]);
        if (!active) return;

        // Mobile stays in normal document flow, but the Google iframe is only
        // revealed once both theme variants have finished painting. The slot
        // is reserved from the first frame, so there is no layout shift and no
        // partially-rendered GIS button visible during route entry.
        host.classList.add("is-ready");
        setLoadError("");
      })
      .catch((error) => {
        if (!active) return;
        host.classList.remove("is-ready");
        setLoadError("Google Sign-In is temporarily unavailable.");
        errorHandlerRef.current?.(error);
      });

    return () => {
      active = false;
      host.classList.remove("is-ready");
      lightTarget.replaceChildren();
      darkTarget.replaceChildren();
    };
  }, [mobileViewport, text]);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className={`google-identity-control ${className}`.trim()}>
      <div className="google-identity-control__button-wrap" aria-disabled={disabled}>
        {mobileViewport ? (
          <div ref={mobileHostRef} className="google-identity-control__mobile-host">
            <div ref={mobileLightRef} className="google-identity-control__mobile-button google-identity-control__mobile-button--light" />
            <div ref={mobileDarkRef} className="google-identity-control__mobile-button google-identity-control__mobile-button--dark" />
          </div>
        ) : (
          <div ref={slotRef} className="google-identity-control__persistent-slot" />
        )}
        {disabled && mobileViewport && <span className="google-identity-control__blocker" aria-hidden="true" />}
      </div>
      {loadError && <small className="google-identity-control__error">{loadError}</small>}
    </div>
  );
}
